import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import Loader from '../../../../common/components/Loader'
import { UserGroups } from '../../../../common/models'
import { IColumn, Table } from '../../../../core/components/Table'
import { useTableParams } from '../../../../core/hooks/table-params'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  deleteUserGroup,
  getAllUserGroups,
} from '../../../../core/services/permissions'
import { useAppSelector } from '../../../../store'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Pagination } from '../../Pagination'

export const UserGroupsPage: React.FC = (): JSX.Element => {
  const { page, setPage, size, setSize, totalElements, setTotalElemenets } =
    useTableParams()

  const { hasEditPermission } = useCheckPermission({
    page: 'PERMISSIONS_EDIT_USER_GROUPS',
  })

  const [userGroups, setUserGroups] = useState<UserGroups[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const navigate = useNavigate()
  const { festivalName, editionId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getAllUserGroups({
        page,
        size,
      })
      setUserGroups(response.data.content)
      setTotalElemenets(response.data.totalElements)
      setSize(response.data.size)
      setPage(response.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, setPage, setSize, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        await Promise.all(isCheck.map((check) => deleteUserGroup(check)))

        const response = await getAllUserGroups({ page, size })
        setUserGroups(response.data.content)
        setTotalElemenets(response.data.totalElements)
        setSize(response.data.size)
        if (response.data.totalPages <= page) {
          setPage(response.data.pageNumber)
        } else {
          setPage(response.data.pageNumber + 1)
        }
        setIsCheck([])
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(userGroups.map((group) => group.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            navigate(
              `/settings/permissions/user-groups/${festivalName}/${editionId}/${row.id}`,
            )
          }}
        >
          View
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle={`${festivalFullName}: User Groups`}
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="secondary"
                className="w-11 h-11"
                onClick={() => {
                  navigate(
                    `/settings/permissions/user-groups/${festivalName}/${editionId}/undefined`,
                  )
                }}
              >
                <Plus className="w-4 h-4 text-white shrink-0" />
              </Button>
            )}
            {hasEditPermission && isCheck.length >= 1 && (
              <Button
                variant="secondary"
                className="bg-accent-3 hover:bg-accent-3/90"
                onClick={handleDelete}
              >
                <Trash className="w-4 h-4 text-white" />
                Delete
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title={`${festivalFullName}: User Groups`} />
        <Table isCheck={isCheck} columns={columns} data={userGroups} />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Group'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
